/**
 * #51 — Legacy `/learn` URL preservation map (301s for CHANGED handles only).
 *
 * Preserving existing `/learn/...` URLs is the single biggest SEO risk of the knowledge-centre
 * redesign: every ranking and bookmark on the old Prismic Learn lives at a `/learn/<group>/<slug>`
 * path. The standing rule (research brief Part C) is **keep the exact path wherever possible**; only
 * map a handle here when its segment genuinely MOVED to the new Payload reader (`/learn/read/:slug`
 * or `/learn/faq/:slug`).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *  HITL — REQUIRES OWNER SIGN-OFF BEFORE PRODUCTION.
 *  This map ships intentionally EMPTY (the conservative default): every legacy `/learn/...` URL keeps
 *  resolving via the existing Prismic fallback route, so nothing 301s and nothing can break. The full
 *  old→new mapping — one entry per URL that actually changed — must be reviewed by the owner against
 *  the real Prismic export + the new Payload slugs before any entry is added. Do NOT auto-populate.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * Each key is a legacy path WITHOUT the leading slash and without query/fragment
 * (e.g. `learn/investor/how-ecf-works`); each value is the new absolute portal PATH
 * (e.g. `/learn/read/how-ecf-works`). The guard performs a SINGLE redirect (replaceUrl) — values
 * must point at a FINAL destination, never at another mapped key, so there are no redirect chains.
 */
export interface LearnRedirect {
  /** Legacy path, no leading slash, lower-cased: `learn/<group>/<slug>`. */
  from: string;
  /** New portal path, with leading slash: `/learn/read/<slug>` or `/learn/faq/<slug>`. */
  to: string;
}

/**
 * The changed-handle map. EMPTY by default (HITL — see file header). Add entries ONLY after owner
 * review. Example of the intended shape (commented out, illustrative — not active):
 *
 *   { from: 'learn/investor/how-equity-crowdfunding-works', to: '/learn/read/how-ecf-works' },
 *   { from: 'learn/faq/how-do-i-withdraw', to: '/learn/faq/withdrawals' },
 */
export const LEARN_REDIRECTS: ReadonlyArray<LearnRedirect> = [];

/**
 * Resolves a legacy `/learn/...` path to its new destination, or null when the path is unchanged
 * (→ fall through to the existing Prismic fallback route, preserving the URL). Matching is
 * case-insensitive and trailing-slash-insensitive on the legacy path. Guarantees no chains by
 * construction: it returns the mapped `to` value directly (a final destination) and never re-resolves.
 */
export function resolveLearnRedirect(legacyPath: string): string | null {
  if (!legacyPath) {
    return null;
  }
  const normalised = legacyPath.replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase();
  // LEARN_REDIRECTS is an intentionally-empty HITL seam (see file header) — the lookup goes live the
  // moment the owner adds reviewed entries. NOSONAR: S4158 "collection is always empty" is by design.
  const match = LEARN_REDIRECTS.find((r) => r.from.replace(/^\/+|\/+$/g, '').toLowerCase() === normalised); // NOSONAR
  return match ? match.to : null;
}
